import { useState } from "react";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import PageHeader from "../components/PageHeader";
import { MapPin } from "lucide-react";

export default function TripSettings() {
  const { config, refreshConfig } = useAuth();
  const [saving, setSaving] = useState(false);
  const [c, setC] = useState({
    trip_name: config?.trip_name || "",
    origin_country: config?.origin_country || "",
    origin_city: config?.origin_city || "",
    start_date: config?.start_date || "",
    end_date: config?.end_date || "",
    currency: config?.currency || "USD",
    budget_goal: config?.budget_goal_cents ? config.budget_goal_cents / 100 : "",
    donate_url: config?.donate_url || "",
    donate_label: config?.donate_label || "",
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/config", {
        trip_name: c.trip_name,
        origin_country: c.origin_country || undefined,
        origin_city: c.origin_city || undefined,
        start_date: c.start_date || undefined,
        end_date: c.end_date || undefined,
        currency: c.currency,
        budget_goal_cents: c.budget_goal ? Math.round(parseFloat(c.budget_goal) * 100) : 0,
        donate_url: c.donate_url,
        donate_label: c.donate_label,
      });
      await refreshConfig();
      toast.success("Trip settings saved");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Trip settings" />

      <div className="bg-white rounded-2xl p-5 shadow-[0_4px_24px_rgba(44,44,62,0.04)] space-y-4" data-testid="trip-settings-card">
        <F label="Trip name" testid="ts-trip-name" value={c.trip_name} onChange={(e) => setC({ ...c, trip_name: e.target.value })} placeholder="RESET 2026 Mission" />

        <div className="rounded-2xl bg-muted/40 p-3 space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium text-[#2C2C3E]"><MapPin className="h-4 w-4 accent-text" /> Home base <span className="text-muted-foreground font-normal">(where the journey begins)</span></div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Home country" testid="ts-origin-country" value={c.origin_country} onChange={(e) => setC({ ...c, origin_country: e.target.value })} placeholder="United States" />
            <F label="Home city (optional)" testid="ts-origin-city" value={c.origin_city} onChange={(e) => setC({ ...c, origin_city: e.target.value })} placeholder="Los Angeles" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <F label="Start date" testid="ts-start" type="date" value={c.start_date || ""} onChange={(e) => setC({ ...c, start_date: e.target.value })} />
          <F label="End date" testid="ts-end" type="date" value={c.end_date || ""} onChange={(e) => setC({ ...c, end_date: e.target.value })} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <F label="Currency" testid="ts-currency" value={c.currency} onChange={(e) => setC({ ...c, currency: e.target.value })} placeholder="USD" />
          <F label="Budget goal" testid="ts-goal" type="number" value={c.budget_goal} onChange={(e) => setC({ ...c, budget_goal: e.target.value })} placeholder="45000" />
        </div>

        <div className="rounded-2xl bg-muted/40 p-3 space-y-3">
          <div className="text-sm font-medium text-[#2C2C3E]">Donate link <span className="text-muted-foreground font-normal">(shown to partners)</span></div>
          <F label="Donation URL" testid="ts-donate-url" value={c.donate_url} onChange={(e) => setC({ ...c, donate_url: e.target.value })} placeholder="https://" />
          <F label="Button label (optional)" testid="ts-donate-label" value={c.donate_label} onChange={(e) => setC({ ...c, donate_label: e.target.value })} placeholder="Give toward the trip" />
        </div>
      </div>

      <Button data-testid="trip-settings-save-btn" disabled={saving} onClick={save} className="w-full mt-6 rounded-full accent-bg text-white h-12 hover:opacity-90">{saving ? "Saving…" : "Save trip settings"}</Button>

      <p className="text-xs text-muted-foreground text-center mt-4">
        Need to add or re-map the countries you're visiting? Open <span className="font-medium text-[var(--brand-navy)]">Trip → Countries</span>.
      </p>
    </div>
  );
}

function F({ label, testid, ...props }) {
  return (
    <div>
      <Label className="text-sm text-[#2C2C3E]">{label}</Label>
      <Input data-testid={testid} className="mt-1.5 rounded-xl h-11" {...props} />
    </div>
  );
}
